import React from 'react';
import { FiAlertCircle, FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import useEnrollmentFlow from './useEnrollmentFlow';
import EnrollmentBrand from './EnrollmentBrand';
import OtpCodeInput from '../auth/OtpCodeInput';
import {
  Container, Card, Title, Subtitle, Form, FormGroup, InputWrapper, Input, Icon,
  PasswordToggle, Button, ErrorMessage, LoadingSpinner, PageSpinner, LinkButton, FooterText
} from './EnrollmentFlow.styles';

function EnrollmentFlow() {
  const {
    email, password, setPassword, loading, error, userExists, checkingUser,
    showPassword, setShowPassword, handleFormSubmit, handleEmailChange,
    authMethod, otpCode, setOtpCode, otpInfoMessage, resendCooldown,
    handleResendCode, handleToggleAuthMethod, goToSignIn, handleChangeEmail
  } = useEnrollmentFlow();

  // Once a code is out (or a sign-in is under way) the address is fixed: editing it silently would
  // leave the code box tied to the old one. "Change" is the way back.
  const emailLocked = userExists !== null;

  if (loading) {
    return (
      <Container>
        <Card
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <PageSpinner />
          <Title>Checking Enrollment Status</Title>
          <Subtitle>Please wait while we verify your enrollment...</Subtitle>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <EnrollmentBrand />
        {userExists === null && <Subtitle>Start by entering your email address</Subtitle>}

        {error && (
          <ErrorMessage>
            <FiAlertCircle />
            {error}
          </ErrorMessage>
        )}

        <Form onSubmit={handleFormSubmit}>
          <FormGroup>
            <InputWrapper>
              <Icon>
                {emailLocked ? <FiLock size={16} /> : <FiMail size={16} />}
              </Icon>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Email address"
                required
                readOnly={emailLocked}
                aria-readonly={emailLocked}
                disabled={checkingUser}
                style={emailLocked ? { opacity: 0.75, cursor: 'default', paddingRight: '5.5rem', background: '#EDEDED' } : undefined}
              />
              {emailLocked && (
                <LinkButton
                  type="button"
                  onClick={handleChangeEmail}
                  aria-label="Change email address"
                  style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)' }}
                >
                  Change
                </LinkButton>
              )}
            </InputWrapper>
          </FormGroup>

          {(userExists === false || (userExists === true && authMethod === 'otp')) && (
            <>
              <Subtitle style={{ marginBottom: '1rem' }}>
                {userExists === false ? (
                  <>
                    To make sure your email is correct, we sent a 6-digit code to <strong>{email}</strong>.
                    Enter it to continue to payment. It expires in 10 minutes.
                  </>
                ) : (
                  <>We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.</>
                )}
              </Subtitle>
              {otpInfoMessage && <Subtitle style={{ marginBottom: '1rem' }}>{otpInfoMessage}</Subtitle>}
              <FormGroup>
                <OtpCodeInput value={otpCode} onChange={setOtpCode} />
              </FormGroup>
            </>
          )}

          {userExists === true && authMethod === 'password' && (
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>
          )}

          <Button type="submit" disabled={loading || checkingUser}>
            {loading ? (
              <>
                <LoadingSpinner />
                {userExists === false ? 'Verifying...' : 'Signing in...'}
              </>
            ) : checkingUser ? (
              <>
                <LoadingSpinner />
                Checking account...
              </>
            ) : userExists === null ? (
              'Continue'
            ) : (userExists === false || authMethod === 'otp') ? (
              'Verify & Continue'
            ) : (
              'Sign In'
            )}
          </Button>

          {userExists === false && (
            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                {resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  <LinkButton type="button" onClick={handleResendCode}>Resend code</LinkButton>
                )}
              </div>
            </div>
          )}

          {userExists === true && (
            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {authMethod === 'otp' && (
                <div style={{ marginBottom: '0.5rem' }}>
                  {resendCooldown > 0 ? (
                    `Resend code in ${resendCooldown}s`
                  ) : (
                    <LinkButton type="button" onClick={handleResendCode}>Resend code</LinkButton>
                  )}
                </div>
              )}
              <LinkButton
                type="button"
                onClick={() => handleToggleAuthMethod(authMethod === 'otp' ? 'password' : 'otp')}
              >
                {authMethod === 'otp' ? 'Use my password instead' : 'Email me a one-time code instead'}
              </LinkButton>
            </div>
          )}
        </Form>

        {userExists === null && (
          <FooterText>
            Existing student? <LinkButton type="button" onClick={goToSignIn}>Sign In</LinkButton>
          </FooterText>
        )}
      </Card>
    </Container>
  );
}

export default EnrollmentFlow;
